import { useState } from "react";
import axios from "axios";
import { Phone, MapPin, Clock, Send, Loader2, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { Reveal } from "@/components/site/Reveal";
import {
  ADDRESS, MAPS_EMBED, MAPS_LINK, PHONE_DISPLAY, PHONE_TEL,
  waLink, onCall, onWhatsApp, onRoute, track, WHATSAPP_MSG,
} from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", vehicle: "", location: "", message: "" };

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-4 py-4 border-b border-white/10">
    <span className="shrink-0 grid place-items-center h-11 w-11 rounded-sm bg-brand/15 text-brand">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
      <div className="text-white font-medium">{children}</div>
    </div>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Informe pelo menos nome e telefone.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { ...form, service: "solicitacao-site" });
      track("generate_lead", { source: "contact-form" });
      toast.success("Solicitação enviada! Vamos te chamar no WhatsApp.");
      const extra = `${WHATSAPP_MSG} ${form.location || ""}. Veículo: ${form.vehicle || "não informado"}. Nome: ${form.name}.`;
      onWhatsApp("contact-form");
      window.open(`https://wa.me/5511922063894?text=${encodeURIComponent(extra)}`, "_blank");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Não foi possível enviar. Ligue ou chame no WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contato" className="relative py-24 md:py-32 bg-surface border-t border-white/10">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <span className="text-brand font-display text-lg font-bold uppercase tracking-[0.2em]">Contato</span>
          <h2 className="mt-3 font-display text-4xl md:text-6xl font-black text-white leading-[0.92]">
            Precisa de Guincho Agora?
          </h2>
          <p className="mt-5 text-lg text-zinc-400">Atendimento 24 horas em Jundiaí e região.</p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 lg:gap-14">
          {/* Left: info + map */}
          <Reveal>
            <div className="space-y-1">
              <InfoRow icon={Phone} label="Telefone e WhatsApp">
                <a href={`tel:${PHONE_TEL}`} onClick={() => onCall("contato-info")} className="hover:text-brand transition-colors">
                  {PHONE_DISPLAY}
                </a>
              </InfoRow>
              <InfoRow icon={MapPin} label="Endereço">
                {ADDRESS.street}<br />
                {ADDRESS.district} — {ADDRESS.city}<br />
                CEP {ADDRESS.zip}
              </InfoRow>
              <InfoRow icon={Clock} label="Horário">
                <span className="text-brand font-bold">ABERTO 24 HORAS</span> · 7 dias por semana
              </InfoRow>
            </div>

            <div className="mt-6 relative overflow-hidden rounded-sm border border-white/10 aspect-[16/10]">
              <iframe
                title="Localização DG Guinchos Jundiaí"
                src={MAPS_EMBED}
                className="absolute inset-0 h-full w-full grayscale-[0.3] contrast-110"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a
              href={MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-maps-btn"
              onClick={() => onRoute("contato")}
              className="mt-3 inline-flex items-center gap-2 border border-white/20 hover:border-brand text-white font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
            >
              <MapPinned className="h-5 w-5 text-brand" /> Abrir no Google Maps
            </a>
          </Reveal>

          {/* Right: form + big CTAs */}
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-3">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-call-btn"
                onClick={() => onCall("contato")}
                className="flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-display font-bold uppercase tracking-wider text-lg py-4 rounded-sm transition-all active:scale-95"
              >
                <Phone className="h-5 w-5" /> Ligar Agora
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                onClick={() => onWhatsApp("contato")}
                className="flex items-center justify-center gap-2 bg-wapp hover:bg-wapp-hover text-black font-display font-bold uppercase tracking-wider text-lg py-4 rounded-sm transition-all active:scale-95"
              >
                <WhatsAppIcon className="h-6 w-6" /> WhatsApp
              </a>
            </div>

            <form onSubmit={submit} data-testid="contact-form" className="mt-6 bg-ink border border-white/10 rounded-sm p-6 space-y-4">
              <h3 className="font-display text-2xl font-bold text-white">Solicitar Atendimento</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-zinc-400">Nome *</Label>
                  <Input id="name" data-testid="form-name" value={form.name} onChange={upd("name")} placeholder="Seu nome" className="bg-surface border-white/15 focus-visible:ring-brand focus-visible:border-brand" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-zinc-400">Telefone *</Label>
                  <Input id="phone" data-testid="form-phone" value={form.phone} onChange={upd("phone")} placeholder="(11) 90000-0000" className="bg-surface border-white/15 focus-visible:ring-brand focus-visible:border-brand" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vehicle" className="text-zinc-400">Veículo</Label>
                  <Input id="vehicle" data-testid="form-vehicle" value={form.vehicle} onChange={upd("vehicle")} placeholder="Carro, moto, modelo..." className="bg-surface border-white/15 focus-visible:ring-brand focus-visible:border-brand" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="location" className="text-zinc-400">Localização</Label>
                  <Input id="location" data-testid="form-location" value={form.location} onChange={upd("location")} placeholder="Cidade / endereço" className="bg-surface border-white/15 focus-visible:ring-brand focus-visible:border-brand" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-zinc-400">Como podemos ajudar?</Label>
                <Textarea id="message" data-testid="form-message" value={form.message} onChange={upd("message")} rows={3} placeholder="Descreva a situação do veículo" className="bg-surface border-white/15 focus-visible:ring-brand focus-visible:border-brand" />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="form-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-display font-bold uppercase tracking-wider text-lg py-4 rounded-sm transition-all active:scale-95"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                {busy ? "Enviando..." : "Solicitar Guincho"}
              </button>
              <p className="text-xs text-zinc-500 text-center">Ao enviar, você será direcionado ao nosso WhatsApp para agilizar o atendimento.</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
